import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Quint, TweenMax } from 'gsap';
import { Auth } from '../../../autentication/Auth';
import { EventTypes } from '../../../shared/EventTypes';
import { SharedService } from '../../../shared/shared.service';
import { AppEventDispatcher } from '../../../utils/AppEventDispatcher';
import { ChangeDigitalSignatureEventType } from '../change-digital-signature.event.type';
import { ChangeDigitalSignatureService } from '../change-digital-signature.service';
import { ChangeDigitalSignature } from '../model/change-digital-signature';

@Component({
	selector: 'app-reset-with-steps',
	templateUrl: './reset-with-steps.component.html',
	styleUrls: ['./reset-with-steps.component.styl'],
	encapsulation: ViewEncapsulation.None
})
export class ResetWithStepsComponent implements OnInit, OnDestroy {

	@Input() submited: boolean;
	@Input() changeDigitalSignatureData: ChangeDigitalSignature;
	@Output() onSetForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
	@Output() showStepButtons: EventEmitter<boolean> = new EventEmitter<boolean>();

	private view = '[data-component="reset-with-steps"]';

	public active = false;
	public disabled = false;
	public formResetSignature: FormGroup;
	public inputToKeypadSendValue: any;
	public labelNextButton = 'Próximo';
	public lastStep = 2;
	public step = 1;
	public title = '';

	constructor(
		private formBuilder: FormBuilder,
		private sharedService: SharedService,
		private changeDigitalSignatureService: ChangeDigitalSignatureService) { }

	ngOnInit() {
		this.setTitleAndLabelButton();
		this.initForm();

		AppEventDispatcher.listen( ChangeDigitalSignatureEventType.STEPS.CHANGE_DG_SIGNATURE_RESET, () => {
			this.inputToKeypadSendValue = this.formResetSignature.get(['signatures', 'signature']);
			this.onSetForm.emit(this.formResetSignature);
			this.active = true;
			this.show();
		});

		this.showStepButtons.emit(false);
	}

	ngOnDestroy() {
		AppEventDispatcher.removeListener( ChangeDigitalSignatureEventType.STEPS.CHANGE_DG_SIGNATURE_RESET);
	}

	private initForm() {
		this.formResetSignature = this.formBuilder.group({
			signatures: this.formBuilder.group({
				signature: ['', [Validators.required]],
				repeatSignature: ['', [Validators.required]],
			}, { validator: this.signatureConfirming }),
		});
	}

	private signatureConfirming(c: AbstractControl): { invalid: boolean } {
		if (c.get('signature').value !== c.get('repeatSignature').value) {
			return { invalid: true };
		}
	}

	public setInputValueFromKeyPad(val: Array<number>) {
		if (this.inputToKeypadSendValue.value.length < 6) {
			this.inputToKeypadSendValue.setValue(this.inputToKeypadSendValue.value.toString() + val[0].toString());
		}
	}

	public onCorrect() {
		this.inputToKeypadSendValue.setValue('');
	}

	public setInputTarget() {
		switch (this.step) {
			case 1:
				this.inputToKeypadSendValue = this.formResetSignature.get(['signatures', 'signature']);
				break;
			case 2:
				this.inputToKeypadSendValue = this.formResetSignature.get(['signatures', 'repeatSignature']);
				break;
		}
	}

	public prev() {
		if (this.step === 1) {
			this.sharedService.changeRerender(true);
		}

		this.step--;
		this.setTitleAndLabelButton();
		this.setInputTarget();
	}

	public next() {
		if (this.step === this.lastStep) {
			this.send();
		} else {
			this.step++;
			this.setTitleAndLabelButton();
			this.setInputTarget();
		}
	}

	private setTitleAndLabelButton() {
		switch (this.step) {
			case 1:
				this.title = 'Digite sua assinatura eletrônica';
				this.labelNextButton = 'Próximo';
				break;
			case 2:
				this.title = 'Digite a confirmação de sua assinatura eletrônica';
				this.labelNextButton = 'Finalizar';
				break;
		}
	}

	private send() {
		this.submited = true;
		if (this.formResetSignature.valid) {

			this.preload(true);
			this.submited = false;
			this.disabled = true;

			let time = 1;
			const timeLoader = setInterval(() => {
				time++;
			}, 1000);

			this.changeDigitalSignatureService.changeDigitalSignature(this.formResetSignature.value).subscribe(data => {

				if (data.Sucesso) {
					this.changeDigitalSignatureData.success = true;
				} else {
					this.changeDigitalSignatureData.success = false;
				}

				this.changeDigitalSignatureData.message = data.Mensagem;

				this.checkAfterSend(timeLoader, time);
				this.disabled = false;
				this.hide();

			}, error => {
				this.changeDigitalSignatureData.success = false;
				this.changeDigitalSignatureData.message = error.error.Mensagem;
				this.checkAfterSend(timeLoader, time);
				this.disabled = false;
				this.hide();
			});
		}
	}

	private show() {
		const interval = setInterval(() => {

			if ($(this.view).length > 0) {
				const containerHeight = this.getStepHeight();

				TweenMax.to('.steps-container', .8, { height: containerHeight, ease: Quint.easeOut});
				TweenMax.to(this.view, .8, { delay: .4, left: 0, ease: Quint.easeOut});
				TweenMax.to('.buttons', .8, {delay: .8, bottom: 0, ease: Quint.easeOut});

				clearInterval(interval);
			}
		}, 10);
	}

	private getStepHeight(): number {
		return $(this.view).outerHeight() + $('.buttons').outerHeight() + 60;
	}

	private hide() {
		TweenMax.delayedCall(1, () => {
			AppEventDispatcher.dispatch(ChangeDigitalSignatureEventType.STEPS.CHANGE_DG_SIGNATURE_WARNING, { show: true });

			TweenMax.to(this.view, .8, {
				delay: .4, left: '-100%', ease: Quint.easeOut, onComplete: () => {
					this.active = false;
				}
			});
		});
	}

	private checkAfterSend(timeLoader, time) {
		if (time > 3) {
			this.preload(false);
		} else {
			TweenMax.delayedCall(3 - time, () => {
				this.preload(false);
			});
		}
		clearInterval(timeLoader);
	}

	private preload(active: boolean, delay = 0) {
		AppEventDispatcher.dispatch(EventTypes.PRELOAD,
			{ element: '[data-component="reset"] .content', show: active, delay: delay });
	}

}



// WEBPACK FOOTER //
// /home/ubuntu/workdir/workspace/Front End Angular (NGINX)/omni-internetbanking/hmg-omni-internetbanking-deploy/src/app/master/change-digital-signature/reset-with-steps/reset-with-steps.component.ts